import OfficialBadge from './community/OfficialBadge';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Eye, Trash2, Loader2, Globe, Users, Star, Bookmark, Send } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Input } from './ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { toast } from 'sonner';
import { storiesApi, STORY_EMOJIS } from '../services/storiesApi';
import { messagesApi } from '../services/messagesApi';
import { mediaUrl } from '../services/postsApi';

const STORY_MS = 5000;

const timeAgo = (iso, es) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return es ? 'ahora' : 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h`;
};

const StoryViewer = ({ tray, startUserIndex = 0, onClose, language = 'en', onSeen }) => {
  const es = language === 'es';
  const [userIdx, setUserIdx] = useState(startUserIndex);
  const [stories, setStories] = useState([]);
  const [storyIdx, setStoryIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [viewersOpen, setViewersOpen] = useState(false);
  const [viewersData, setViewersData] = useState(null);
  const [myReaction, setMyReaction] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [pinnedIds, setPinnedIds] = useState(new Set());
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [answers, setAnswers] = useState({});
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const viewedRef = useRef(new Set());

  const groups = tray || [];
  const currentGroup = groups[userIdx];
  const story = stories[storyIdx];

  const closeAll = useCallback(() => { onSeen?.(); onClose(); }, [onClose, onSeen]);

  const nextUser = useCallback(() => {
    if (userIdx < groups.length - 1) { setUserIdx((i) => i + 1); } else { closeAll(); }
  }, [userIdx, groups.length, closeAll]);

  const prevUser = useCallback(() => {
    if (userIdx > 0) setUserIdx((i) => i - 1);
  }, [userIdx]);

  useEffect(() => {
    if (!currentGroup) return;
    let cancelled = false;
    setLoading(true); setStoryIdx(0); setProgress(0);
    (async () => {
      try {
        const list = await storiesApi.userStories(currentGroup.user_id);
        if (cancelled) return;
        if (!list.length) { nextUser(); return; }
        setStories(list);
      } catch { if (!cancelled) nextUser(); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [userIdx, currentGroup?.user_id]); // eslint-disable-line

  const goNext = useCallback(() => {
    setProgress(0);
    if (storyIdx < stories.length - 1) setStoryIdx((i) => i + 1);
    else nextUser();
  }, [storyIdx, stories.length, nextUser]);

  const goPrev = useCallback(() => {
    setProgress(0);
    if (storyIdx > 0) setStoryIdx((i) => i - 1);
    else prevUser();
  }, [storyIdx, prevUser]);

  // Sync reaction state when the current story changes + record view.
  useEffect(() => {
    if (loading || !story) return;
    setMyReaction(story.my_reaction || null);
    setReplyText('');
    if (!story.is_me && !viewedRef.current.has(story.id)) {
      viewedRef.current.add(story.id);
      storiesApi.view(story.id).catch(() => {});
    }
  }, [loading, story?.id]); // eslint-disable-line

  // Progress timer.
  useEffect(() => {
    if (loading || !story) return;
    startRef.current = performance.now();
    const tick = (t) => {
      if (paused) { startRef.current = t - progress * STORY_MS; rafRef.current = requestAnimationFrame(tick); return; }
      const elapsed = t - startRef.current;
      const p = Math.min(1, elapsed / STORY_MS);
      setProgress(p);
      if (p >= 1) goNext();
      else rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [loading, story?.id, paused]); // eslint-disable-line

  const react = async (emoji) => {
    if (!story || story.is_me) return;
    const prev = myReaction;
    const next = prev === emoji ? null : emoji;
    setMyReaction(next);
    try { const r = await storiesApi.react(story.id, emoji); setMyReaction(r.my_reaction); }
    catch { setMyReaction(prev); toast.error(es ? 'Error' : 'Something went wrong'); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !story) return;
    setSending(true);
    try {
      if (story.is_daily_verse) {
        const res = await storiesApi.answer(story.id, replyText.trim());
        setAnswers((a) => ({ ...a, [story.id]: res.my_answer }));
        setReplyText('');
        toast.success(es ? 'Gracias por compartir 🙏' : 'Thank you for sharing 🙏');
        return;
      }
      await messagesApi.storyReply(story.id, replyText.trim());
      setReplyText('');
      toast.success(es ? 'Mensaje enviado' : 'Message sent');
    } catch (e) { toast.error(e?.response?.data?.detail || (es ? 'No se pudo enviar' : 'Could not send')); }
    finally { setSending(false); }
  };

  const saveStory = async () => {
    if (!story) return;
    try { await storiesApi.save(story.id); setSavedIds((s) => new Set(s).add(story.id)); toast.success(es ? 'Guardada' : 'Saved'); }
    catch { toast.error(es ? 'Error' : 'Something went wrong'); }
  };

  const pinStory = async () => {
    if (!story) return;
    try { await storiesApi.pin(story.id); setPinnedIds((s) => new Set(s).add(story.id)); toast.success(es ? 'Añadida a destacadas' : 'Added to Highlights'); }
    catch { toast.error(es ? 'Error' : 'Something went wrong'); }
  };

  const openViewers = async () => {
    if (!story?.is_me) return;
    setPaused(true); setViewersOpen(true); setViewersData(null);
    try { setViewersData(await storiesApi.viewers(story.id)); } catch { setViewersData({ count: 0, viewers: [] }); }
  };

  const deleteStory = async () => {
    if (!story) return;
    try {
      await storiesApi.remove(story.id);
      toast.success(es ? 'Historia eliminada' : 'Story deleted');
      const rest = stories.filter((s) => s.id !== story.id);
      if (!rest.length) nextUser(); else { setStories(rest); setStoryIdx((i) => Math.min(i, rest.length - 1)); setProgress(0); }
    } catch { toast.error(es ? 'Error' : 'Something went wrong'); }
  };

  if (!currentGroup) return null;
  const initials = (currentGroup.author_name || '?').slice(0, 2).toUpperCase();
  const isSaved = story && savedIds.has(story.id);
  const isPinned = story && pinnedIds.has(story.id);

  return (
    <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center select-none" data-testid="story-viewer">
      <div className="relative w-full h-full max-w-md mx-auto flex flex-col">
        {/* Progress bars */}
        <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-2">
          {stories.map((s, i) => (
            <div key={s.id} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden">
              <div className="h-full bg-white transition-none"
                style={{ width: i < storyIdx ? '100%' : i === storyIdx ? `${progress * 100}%` : '0%' }} />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-3 left-0 right-0 z-20 flex items-center gap-3 px-3 pt-3">
          <Avatar className="w-9 h-9 ring-2 ring-white/60">
            {currentGroup.author_picture ? <AvatarImage src={currentGroup.author_picture} /> : null}
            <AvatarFallback className="text-xs bg-white/20 text-white">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate flex items-center gap-1">{currentGroup.author_name}{currentGroup.is_official && <OfficialBadge className="w-3.5 h-3.5" />}</p>
            <p className="text-[11px] text-white/70 flex items-center gap-1">
              {timeAgo(story?.created_at, es)}
              {story?.visibility === 'friends' ? <Users className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
            </p>
          </div>
          <button onClick={closeAll} className="text-white/90 p-1" data-testid="story-close-btn"><X className="w-6 h-6" /></button>
        </div>

        {/* Media */}
        <div className="flex-1 flex items-center justify-center relative"
          onPointerDown={() => setPaused(true)} onPointerUp={() => setPaused(false)} onPointerLeave={() => setPaused(false)}>
          {loading || !story ? (
            <Loader2 className="w-8 h-8 animate-spin text-white/70" />
          ) : (
            <img src={mediaUrl(story.image_url)} alt={story.caption || ''} className="max-h-full max-w-full object-contain" draggable={false} />
          )}
          <button className="absolute left-0 top-0 bottom-0 w-1/3 z-10" onClick={goPrev} data-testid="story-prev-zone" aria-label="previous" />
          <button className="absolute right-0 top-0 bottom-0 w-2/3 z-10" onClick={goNext} data-testid="story-next-zone" aria-label="next" />
        </div>

        {/* Caption */}
        {story?.caption && (
          <div className="absolute bottom-28 left-0 right-0 px-6 z-20 text-center">
            <p className="text-white text-sm bg-black/40 rounded-xl px-3 py-2 inline-block">{story.caption}</p>
          </div>
        )}

        {/* Footer controls */}
        {story?.is_me ? (
          <div className="absolute bottom-4 left-0 right-0 z-20 flex items-center justify-center gap-6 px-6">
            <button onClick={openViewers} className="flex items-center gap-1.5 text-white text-sm" data-testid="story-viewers-btn">
              <Eye className="w-5 h-5" /> {story.viewers_count ?? 0} {es ? 'vistas' : 'views'}
            </button>
            <button onClick={pinStory} disabled={isPinned} className="flex items-center gap-1.5 text-white/90 text-sm disabled:opacity-60" data-testid="story-pin-btn">
              <Star className={`w-5 h-5 ${isPinned ? 'fill-amber-400 text-amber-400' : ''}`} /> {es ? 'Destacar' : 'Highlight'}
            </button>
            <button onClick={deleteStory} className="flex items-center gap-1.5 text-white/90 text-sm" data-testid="story-delete-btn">
              <Trash2 className="w-5 h-5" /> {es ? 'Eliminar' : 'Delete'}
            </button>
          </div>
        ) : story ? (
          <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pb-4 pt-2 bg-gradient-to-t from-black/70 to-transparent space-y-2">
            {/* Reaction bar */}
            <div className="flex items-center justify-center gap-2" data-testid="story-reaction-bar" onPointerDown={(e) => e.stopPropagation()}>
              {STORY_EMOJIS.map((em) => (
                <button key={em} onClick={() => react(em)}
                  className={`text-2xl transition-transform ${myReaction === em ? 'scale-125 drop-shadow-lg' : 'opacity-80 hover:scale-110'}`}
                  data-testid={`story-react-${em}`}>{em}</button>
              ))}
            </div>
            {/* Daily verse question (answers are private to Lumera) */}
            {story.is_daily_verse && (
              <div className="rounded-2xl bg-black/40 px-3 py-2 text-center text-white" data-testid="story-daily-question" onPointerDown={(e) => e.stopPropagation()}>
                <p className="text-sm font-semibold">{story.question}</p>
                {(answers[story.id] || story.my_answer) ? (
                  <p className="mt-1 text-xs text-white/80" data-testid="story-my-answer">
                    {es ? 'Tu respuesta: ' : 'Your answer: '}“{answers[story.id] || story.my_answer}”
                  </p>
                ) : (
                  <p className="mt-0.5 text-[11px] text-white/60">{es ? 'Solo Lumera ve tu respuesta' : 'Only Lumera sees your answer'}</p>
                )}
              </div>
            )}
            {/* Reply + save */}
            <div className="flex items-center gap-2">
              <Input value={replyText} onChange={(e) => setReplyText(e.target.value)}
                onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}
                onKeyDown={(e) => { if (e.key === 'Enter') sendReply(); }}
                placeholder={story.is_daily_verse
                  ? (es ? 'Escribe tu respuesta…' : 'Write your answer…')
                  : (es ? `Responder a ${currentGroup.author_name}…` : `Reply to ${currentGroup.author_name}…`)}
                className="h-10 bg-white/10 border-white/30 text-white placeholder:text-white/60 rounded-full"
                data-testid="story-reply-input" />
              <button onClick={sendReply} disabled={sending || !replyText.trim()} className="text-white p-2 disabled:opacity-40" data-testid="story-reply-send">
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
              <button onClick={saveStory} disabled={isSaved} className="text-white p-2 disabled:opacity-60" data-testid="story-save-btn">
                <Bookmark className={`w-5 h-5 ${isSaved ? 'fill-white' : ''}`} />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Viewers list */}
      <Sheet open={viewersOpen} onOpenChange={(v) => { setViewersOpen(v); if (!v) setPaused(false); }}>
        <SheetContent side="bottom" className="max-h-[70vh] overflow-y-auto" data-testid="story-viewers-sheet">
          <SheetHeader>
            <SheetTitle>{es ? 'Vistas' : 'Viewers'} {viewersData ? `· ${viewersData.count}` : ''}</SheetTitle>
            <SheetDescription className="sr-only">{es ? 'Personas que vieron esta historia' : 'People who viewed this story'}</SheetDescription>
          </SheetHeader>
          {!viewersData ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : viewersData.viewers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">{es ? 'Aún nadie la ha visto' : 'No views yet'}</p>
          ) : (
            <div className="space-y-3 pt-3">
              {viewersData.viewers.map((v) => (
                <div key={v.user_id} className="flex items-center gap-3">
                  <Avatar className="w-8 h-8">
                    {v.picture ? <AvatarImage src={v.picture} /> : null}
                    <AvatarFallback className="text-xs bg-primary/15">{(v.name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm">{v.name}</span>
                  {v.reaction && <span className="text-base">{v.reaction}</span>}
                  <span className="ml-auto text-xs text-muted-foreground">{timeAgo(v.viewed_at, es)}</span>
                </div>
              ))}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default StoryViewer;
